import React, { useEffect, useRef, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Button,
  ButtonBase,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import moment from "moment";
import { GetOnlinePolicyRecordings } from "../../../../Service/_index";
import { shortLabel } from "../utils/policyParts";
import { jobStatusLabel } from "../utils/dateHelpers";
import SegmentedFilter from "./SegmentedFilter";

const NAVY = "#1E3A5F";
// the small caps captions of the dialog ("BROWSER TABS IN THIS RUN", "RECORDED")
const CAPTION_SX = { fontSize: "11px", fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: "#667085", mb: 1 };

/** The run status chip's colours: green done, red failed, amber in between, grey otherwise. */
const statusTone = (status) => {
  const s = String(status || "").toLowerCase();
  if (s === "completed" || s === "complete") return { color: "#027A48", bg: "#ECFDF3" };
  if (s.startsWith("failed") || s === "error") return { color: "#B42318", bg: "#FEF3F2" };
  if (s === "completed_with_errors" || s === "processing" || s === "hold") return { color: "#B54708", bg: "#FFFAEB" };
  return { color: "#475467", bg: "#F2F4F7" };
};

const fmt = (d) => (d ? moment(d).format("DD MMM YYYY, hh:mm A") : "—");

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return "—";
  const total = Math.round(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const fmtSize = (bytes) =>
  bytes ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : "—";

const fmtClock = (sec) => {
  const total = Math.max(0, Math.round(sec || 0));
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, "0")}`;
};

/**
 * The screen recording of a policy's automation run.
 *
 * There is only ever ONE per part: re-running a part replaces its previous
 * video rather than adding to it, so this shows the most recent run of each
 * (OD, TP and PA are separate runs and keep separate videos). S3
 * deletes the file 10 days after it was made, after which the entry is still
 * listed but has no playable link.
 *
 * The video follows the browser tab the automation is working in, so the KYC /
 * payment tab a portal opens is in it. When a run used more than one tab, each
 * tab is a numbered step under the video that seeks it to that moment; the step
 * now playing is highlighted (layout picked by the owner from three mockups,
 * 2026-10-27).
 *
 * The link is presigned and short-lived, so it is fetched each time the dialog
 * opens rather than cached.
 */
const JobRecordingDialog = ({ open, onClose, policyId, customerName }) => {
  const [loading, setLoading] = useState(false);
  const [recordings, setRecordings] = useState([]);
  const [error, setError] = useState(null);
  const [partKey, setPartKey] = useState("");
  const videoRef = useRef(null);

  useEffect(() => {
    if (!open || !policyId) return;
    let cancelled = false;
    setPartKey("");

    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await GetOnlinePolicyRecordings(policyId);
        if (cancelled) return;
        if (res?.success === false || res?.error) {
          setError(res?.message || "Could not load the recordings");
          setRecordings([]);
        } else {
          setRecordings(res?.recordings || []);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "Could not load the recordings");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, policyId]);

  // The API returns newest-first, and each part keeps one recording. Entries
  // with no `part` (older jobs) count as one.
  const latest = recordings.filter(
    (r, i) => recordings.findIndex((x) => (x.part || "") === (r.part || "")) === i
  );
  const current = latest.find((r) => (r.part || "") === partKey) || latest[0] || null;
  const chapters = Array.isArray(current?.chapters) ? current.chapters : [];

  const jumpTo = (sec) => {
    const el = videoRef.current;
    if (!el) return;
    el.currentTime = Math.max(0, sec || 0);
    el.play().catch(() => {});
  };
  // the browser tab the video is showing now: the last one started at or before the play position
  const [playingAt, setPlayingAt] = useState(0);
  const playingChapter = chapters.reduce((at, c, i) => (Number(c.atSec || 0) <= playingAt ? i : at), 0);
  const tone = statusTone(current && current.jobStatus);
  const partLabel = (r) => (r && r.part ? shortLabel(r.part) : "Policy");

  return (
    <Dialog
      open={!!open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "14px", m: { xs: 1.5, sm: 4 }, width: { xs: "calc(100% - 24px)", sm: undefined } } }}
    >
      <Box sx={{ p: { xs: 2, sm: 2.5 } }} data-testid="recording-dialog">
        {/* Header: the icon tile, whose recording, which part */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: "11px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: NAVY, backgroundColor: "#EEF3F9", border: "1px solid #C9D7E8" }}>
            <VideocamIcon />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "16px", color: NAVY, lineHeight: 1.3 }}>Automation Recording</Typography>
            <Typography sx={{ fontSize: "12.5px", color: "#475467" }}>
              {customerName || "Policy"}
              {current && current.part ? ` · ${partLabel(current)}` : ""}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} aria-label="Close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: NAVY }} />
          </Box>
        )}

        {!loading && error && (
          <Typography sx={{ color: "#B42318", fontSize: "13px", py: 3 }}>{error}</Typography>
        )}

        {!loading && !error && recordings.length === 0 && (
          <EmptyBox
            title="No recording for this policy"
            text="Recordings are kept for 10 days, and only exist for runs made while recording was switched on."
          />
        )}

        {!loading && !error && recordings.length > 0 && (
          <>
            {/* OD / TP / PA keep separate videos: one joined switch */}
            {latest.length > 1 && (
              <SegmentedFilter
                options={latest.map((r) => ({ key: r.part || "", label: partLabel(r) }))}
                value={(current && current.part) || ""}
                onChange={(key) => {
                  setPartKey(key);
                  setPlayingAt(0);
                }}
                testId="recording-part"
                sx={{ mb: 1.5 }}
              />
            )}

            {current?.videoUrl ? (
              <Box
                component="video"
                key={current.videoUrl}
                ref={videoRef}
                src={current.videoUrl}
                controls
                autoPlay={false}
                onTimeUpdate={(e) => setPlayingAt(e.currentTarget.currentTime || 0)}
                sx={{ display: "block", width: "100%", maxHeight: "60vh", borderRadius: "12px", backgroundColor: "#000" }}
              />
            ) : (
              <EmptyBox dashed text="This recording is no longer available — recordings are deleted automatically after 10 days." />
            )}

            {/* The browser tabs of the run, as numbered steps: a click jumps the video there */}
            {current?.videoUrl && chapters.length > 1 && (
              <Box sx={{ mt: 2 }}>
                <Typography sx={CAPTION_SX}>Browser tabs in this run — click to jump</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: `repeat(${Math.min(chapters.length, 4)}, minmax(0, 1fr))` }, gap: 1 }} data-testid="recording-chapters">
                  {chapters.map((c, i) => {
                    const kyc = !!c.kyc;
                    const on = i === playingChapter;
                    return (
                      <ButtonBase
                        key={`${c.atSec}-${i}`}
                        onClick={() => jumpTo(c.atSec)}
                        aria-current={on ? "true" : undefined}
                        sx={{
                          justifyContent: "flex-start",
                          textAlign: "left",
                          gap: 1.25,
                          px: 1.5,
                          py: 1,
                          borderRadius: "10px",
                          border: `1px solid ${on ? (kyc ? "#B54708" : NAVY) : kyc ? "#FEDF89" : "#EAECF0"}`,
                          backgroundColor: on ? (kyc ? "#FEF0C7" : "#EEF3F9") : kyc ? "#FFFAEB" : "#fff",
                          "&:hover": { borderColor: kyc ? "#B54708" : NAVY },
                        }}
                      >
                        <Box sx={{ width: 24, height: 24, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 700, color: "#fff", backgroundColor: kyc ? "#B54708" : NAVY }}>
                          {i + 1}
                        </Box>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: "13px", color: NAVY, lineHeight: 1.3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {kyc ? "KYC" : c.label || "Tab"}
                          </Typography>
                          <Typography sx={{ fontSize: "12px", color: "#667085" }}>{fmtClock(c.atSec)}</Typography>
                        </Box>
                      </ButtonBase>
                    );
                  })}
                </Box>
              </Box>
            )}

            {/* Recorded / Length / Size / Run status */}
            {current && (
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, minmax(0, 1fr))" }, gap: 1, mt: 1.5 }} data-testid="recording-details">
                <DetailTile label="Recorded" value={fmt(current.startedAt)} />
                <DetailTile label="Length" value={fmtDuration(current.durationMs)} />
                <DetailTile label="Size" value={fmtSize(current.sizeBytes)} />
                <DetailTile
                  label="Run status"
                  value={
                    current.jobStatus ? (
                      <Box component="span" sx={{ display: "inline-block", px: 1, py: 0.15, borderRadius: "12px", fontSize: "12px", fontWeight: 700, color: tone.color, backgroundColor: tone.bg }}>
                        ● {jobStatusLabel(current.jobStatus)}
                      </Box>
                    ) : (
                      "—"
                    )
                  }
                />
              </Box>
            )}
          </>
        )}

        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: 2, pt: 1.5, borderTop: "1px solid #EAECF0" }}>
          <Typography sx={{ fontSize: "12px", color: "#667085" }}>
            {recordings.length ? "Kept for 10 days after the run." : ""}
          </Typography>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px", boxShadow: "none", backgroundColor: NAVY, "&:hover": { backgroundColor: "#2E5A8F", boxShadow: "none" } }}
          >
            Close
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

/** One of the four details under the video. */
const DetailTile = ({ label, value }) => (
  <Box sx={{ border: "1px solid #EAECF0", borderRadius: "10px", px: 1.5, py: 1, backgroundColor: "#F9FAFB", minWidth: 0 }}>
    <Typography sx={{ ...CAPTION_SX, mb: 0.25 }}>{label}</Typography>
    <Typography component="div" sx={{ fontSize: "13px", fontWeight: 700, color: NAVY }}>{value}</Typography>
  </Box>
);

/** No video: the reason, centred. */
const EmptyBox = ({ title, text, dashed = false }) => (
  <Box sx={{ textAlign: "center", py: 5, px: 2, color: "#667085", borderRadius: "12px", border: dashed ? "1px dashed #D0D5DD" : "none" }}>
    <VideocamOffIcon sx={{ fontSize: 38, mb: 1, opacity: 0.5 }} />
    {title ? <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#475467" }}>{title}</Typography> : null}
    <Typography sx={{ fontSize: "12.5px", mt: 0.5 }}>{text}</Typography>
  </Box>
);

export default JobRecordingDialog;
